import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import BulkImportDrawer from "../../components/BulkImportDrawer";
import MultiSelect from "../../components/MultiSelect";
import SearchableSelect from "../../components/SearchableSelect";
import Pagination from "../../components/Pagination";
import { useApi } from "../../hooks/useApi";
import useScopedBase from "../../hooks/useScopedBase";
import { useAuth } from "../../contexts/AuthContext";
import {
  listAllBusinesses,
  businessStats,
  createBusiness,
  updateBusiness,
  deleteBusiness,
  bulkImportBusinesses,
  downloadBulkImportTemplate,
  exportBusinesses,
} from "../../api/businessService";
import { listClientPrefixes, nextClientId } from "../../api/clientPrefixService";
import { listTaskMasters } from "../../api/taskMasterService";
import { paginatedRows, paginatedMeta } from "../../utils/pagination";
import { formatDate, initials, serialNumber } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";

// "Clients" is now a flat list of businesses — each business stands on its
// own as a client record. The admin-entered `client_id` (free-form short
// string, unique) is the canonical key for identification.
export default function ClientsPage() {
  const navigate = useNavigate();
  const base = useScopedBase();
  const { hasPermission, role } = useAuth();
  const canAdd = hasPermission("client.add");
  const canEdit = hasPermission("client.edit");
  const canDelete = role === "admin";
  const canExport = role === "admin";
  const [exporting, setExporting] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all"); // all | active | inactive
  const [taskMasterIds, setTaskMasterIds] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [drawer, setDrawer] = useState(null); // null | { mode: 'create' } | { mode: 'edit', business }
  const [bulkOpen, setBulkOpen] = useState(false);

  // Listing task masters needs `client.task.oneoff`, which this page does not
  // otherwise require — skip the fetch (and hide the filter) rather than let a
  // client.view-only employee eat a 403 and get a silently empty dropdown.
  const canFilterByMaster = hasPermission("client.task.oneoff");
  const taskMasters = useApi(
    () => listTaskMasters({ per_page: 500 }),
    [],
    { immediate: canFilterByMaster },
  );
  const masterOptions = useMemo(
    () =>
      paginatedRows(taskMasters.data).map((m) => ({
        id: m.id,
        label: m.name,
        hint: m.task_group?.name,
      })),
    [taskMasters.data],
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, activeFilter, perPage, taskMasterIds]);

  // One param builder for the table and the CSV export, so the download can't
  // drift from what's on screen.
  const listParams = useMemo(() => {
    const params = {};
    if (debouncedSearch) params.search = debouncedSearch;
    if (activeFilter === "active") params.is_active = 1;
    if (activeFilter === "inactive") params.is_active = 0;
    if (taskMasterIds.length) params.task_master_id = taskMasterIds;
    return params;
  }, [debouncedSearch, activeFilter, taskMasterIds]);

  const businesses = useApi(
    () => listAllBusinesses({ ...listParams, page, per_page: perPage }),
    [listParams, page, perPage],
  );
  const stats = useApi(() => businessStats());
  const rows = paginatedRows(businesses.data);
  const meta = paginatedMeta(businesses.data);
  const counts = stats.data?.data || { total: 0, active: 0, inactive: 0, with_login: 0 };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportBusinesses(listParams);
      showToast("success", "Export downloaded");
    } catch (err) {
      showApiError(err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Clients</h1>
          <div className="page-sub">{meta?.total ?? rows.length} clients</div>
        </div>
        <div className="page-actions">
          {canExport && (
            <button className="btn btn-secondary" onClick={handleExport} disabled={exporting}>
              <Icon name="download" size={14} /> {exporting ? "Exporting…" : "Export CSV"}
            </button>
          )}
          {canAdd && (
            <>
              <button className="btn btn-secondary" onClick={() => setBulkOpen(true)}>
                <Icon name="upload" size={14} /> Bulk import
              </button>
              <button className="btn btn-primary" onClick={() => setDrawer({ mode: "create" })}>
                <Icon name="plus" size={14} /> Add client
              </button>
            </>
          )}
        </div>
      </div>

      <div className="filter-bar">
        {[
          { id: "all", label: "All", count: counts.total },
          { id: "active", label: "Active", count: counts.active },
          { id: "inactive", label: "Inactive", count: counts.inactive },
        ].map((f) => (
          <button key={f.id} className={`chip ${activeFilter === f.id ? "active" : ""}`} onClick={() => setActiveFilter(f.id)}>
            {f.label} <span className="chip-count">{f.count}</span>
          </button>
        ))}
        {canFilterByMaster && masterOptions.length > 0 && (
          <MultiSelect
            value={taskMasterIds}
            onChange={setTaskMasterIds}
            options={masterOptions}
            placeholder="All task masters"
            searchPlaceholder="Search task masters…"
            width={240}
          />
        )}
        <div style={{ position: "relative", flex: 1, maxWidth: 360, marginLeft: "auto" }}>
          <Icon
            name="search"
            size={13}
            style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-400)", pointerEvents: "none" }}
          />
          <input
            className="input"
            style={{ paddingLeft: 30 }}
            placeholder="Search by client ID, name, email or phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-body no-pad">
          {businesses.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={5} /></div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon="users"
              title="No clients match"
              hint={
                taskMasterIds.length
                  ? "No client has any of the selected task masters attached."
                  : debouncedSearch
                    ? "Try a different search."
                    : "Add your first client to get started."
              }
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 50 }} className="t-num">#</th>
                  <th>Client ID</th>
                  <th>Client</th>
                  <th>Business</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Active</th>
                  <th>Login</th>
                  <th>Onboarded</th>
                  <th style={{ width: 140 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b, i) => (
                  <tr key={b.id} className="clickable" onClick={() => navigate(`${base}/businesses/${b.id}`)}>
                    <td className="t-num t-muted">{serialNumber(meta, i)}</td>
                    <td className="t-mono fw-600">{b.client_id}</td>
                    <td>
                      <div className="row">
                        <div className="avatar avatar-sm" style={{ background: `hsl(${(b.id * 47) % 360} 60% 55%)` }}>
                          {initials(b.client_name || b.business_name)}
                        </div>
                        <div>
                          <div className="t-primary">{b.client_name || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="t-primary">{b.business_name}</td>
                    <td className="t-muted">{b.business_email}</td>
                    <td className="t-mono">{b.business_phone}</td>
                    <td>
                      {b.is_active ? (
                        <span className="badge badge-success">Active</span>
                      ) : (
                        <span className="badge badge-neutral">Inactive</span>
                      )}
                    </td>
                    <td>
                      {b.user_id ? (
                        <span className="badge badge-info">Enabled</span>
                      ) : (
                        <span className="badge badge-warning">No login</span>
                      )}
                    </td>
                    <td className="t-muted">{formatDate(b.created_at)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
                        <button
                          className="icon-btn"
                          title="View"
                          onClick={() => navigate(`${base}/businesses/${b.id}`)}
                        >
                          <Icon name="eye" size={13} />
                        </button>
                        {canEdit && (
                          <button
                            className="icon-btn"
                            title="Edit"
                            onClick={() => setDrawer({ mode: "edit", business: b })}
                          >
                            <Icon name="edit" size={13} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="icon-btn"
                            title="Delete"
                            onClick={async () => {
                              if (!window.confirm(`Delete client "${b.business_name}"?`)) return;
                              try {
                                await deleteBusiness(b.id);
                                showToast("success", "Client deleted");
                                businesses.reload();
                                stats.reload();
                              } catch (err) {
                                showApiError(err);
                              }
                            }}
                            style={{ color: "var(--danger)" }}
                          >
                            <Icon name="trash" size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pagination
            meta={meta}
            page={page}
            onPageChange={setPage}
            perPage={perPage}
            onPerPageChange={setPerPage}
            disabled={businesses.loading}
          />
        </div>
      </div>

      <ClientDrawer
        open={!!drawer}
        mode={drawer?.mode}
        business={drawer?.business}
        onClose={() => setDrawer(null)}
        onSaved={() => {
          setDrawer(null);
          businesses.reload();
          stats.reload();
        }}
      />

      <BulkImportDrawer
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onImported={() => {
          businesses.reload();
          stats.reload();
        }}
        title="Bulk import clients"
        subtitle="Upload an xlsx filled with one client per row. Existing Client IDs are skipped."
        instructions="The xlsx has 21 columns and a legend block with the allowed values for each dropdown. Delete the legend block before importing."
        downloadFn={downloadBulkImportTemplate}
        importFn={bulkImportBusinesses}
      />
    </>
  );
}

// Allowed values mirror the importer's `in:` validation. Keep in sync.
const ENTITY_TYPES = [
  "Private Limited Company",
  "Public Limited Company",
  "One Person Company",
  "Partnership Firm",
  "Foreign Company",
  "Trust",
  "Society",
  "Association of Person (AOP)",
  "Body of Individual (BOI)",
  "Hindu Undivided Family (HUF)",
  "Individual",
  "Limited Liability Partnership (LLP)",
];
const ITR_TYPES = ["ITR-1", "ITR-2", "ITR-3", "ITR-4", "ITR-5", "ITR-6", "ITR-7"];

const EMPTY_FORM = {
  client_id: "",
  client_name: "",
  business_name: "",
  business_email: "",
  business_phone: "",
  concerned_person_name: "",
  concerned_person_phone: "",
  address: "",
  description: "",
  is_active: true,
  // Tax / IT portal
  office_file_no: "",
  pan: "",
  it_password: "",
  itr_type: "",
  regime: "",
  // GST
  gstin: "",
  state_name: "",
  state_code: "",
  gst_id: "",
  gst_password: "",
  gst_frequency: "",
  gst_scheme: "",
  // Entity
  entity_type: "",
  authorised_signatory: "",
  // TAN / TRACES
  tan_no: "",
  tan_it_password: "",
  tan_traces_id: "",
  tan_traces_password: "",
};

function hydrate(business) {
  if (!business) return { ...EMPTY_FORM };
  const out = { ...EMPTY_FORM };
  for (const k of Object.keys(EMPTY_FORM)) {
    if (k === "is_active") {
      out.is_active = business.is_active === undefined ? true : !!business.is_active;
    } else {
      out[k] = business[k] ?? "";
    }
  }
  return out;
}

// Convert "" → null before POSTing so the backend's regex / `in:` rules
// don't fire on empty strings (and so blanks clear stored values).
function normaliseForSubmit(form) {
  const out = {};
  for (const [k, v] of Object.entries(form)) {
    out[k] = v === "" ? null : v;
  }
  return out;
}

function ClientDrawer({ open, mode = "create", business, onClose, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [prefixes, setPrefixes] = useState([]);
  const [prefixId, setPrefixId] = useState("");
  const [suggesting, setSuggesting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(mode === "edit" && business ? hydrate(business) : { ...EMPTY_FORM });
    setErrors({});
    setPrefixId("");
    // Load prefixes in both modes — admins can re-key a client_id during an
    // edit by picking a different prefix (the next available number for that
    // prefix is fetched and filled in, same flow as create).
    listClientPrefixes()
      .then((res) => setPrefixes(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setPrefixes([]));
  }, [open, mode, business]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Picking a prefix fetches the next incremented id for it and fills it in.
  const onPickPrefix = async (id) => {
    setPrefixId(id);
    if (!id) return;
    setSuggesting(true);
    try {
      const res = await nextClientId(id);
      const next = res?.data?.next_client_id;
      if (next) set({ client_id: next });
    } catch (err) {
      showApiError(err);
    } finally {
      setSuggesting(false);
    }
  };

  const submit = async () => {
    setErrors({});
    setSaving(true);
    try {
      const payload = normaliseForSubmit(form);
      if (mode === "edit") {
        await updateBusiness(business.id, payload);
        showToast("success", "Client updated");
      } else {
        await createBusiness(payload);
        showToast("success", "Client created");
      }
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      wide
      title={mode === "edit" ? `Edit ${business?.business_name || "client"}` : "New client"}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={saving || !form.client_id.trim() || !form.client_name.trim() || !form.business_name.trim()}
          >
            {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Create"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <Section title="Basics" defaultOpen>
          <Field label="Client ID" error={errors.client_id?.[0]} hint="Short identifier (e.g. SCA-NR-00001). Must be unique.">
            {prefixes.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <SearchableSelect
                  value={prefixId}
                  onChange={(v) => onPickPrefix(v ?? "")}
                  options={prefixes.map((p) => ({ id: p.id, label: p.code }))}
                  placeholder="Prefix… (auto-number)"
                  searchPlaceholder="Search prefixes…"
                  width="100%"
                  disabled={suggesting}
                />
              </div>
            )}
            <input
              className="input"
              value={form.client_id}
              onChange={(e) => set({ client_id: e.target.value })}
              placeholder="SCA-NR-00001"
            />
          </Field>
          <Field label="Office File No" error={errors.office_file_no?.[0]} hint="Internal serial — separate from Client ID.">
            <input className="input" value={form.office_file_no} onChange={(e) => set({ office_file_no: e.target.value })} placeholder="1" />
          </Field>
          <Field label="Legal name of assessee" error={errors.client_name?.[0]} hint="Proprietor / firm name — who you bill and correspond with.">
            <input className="input" value={form.client_name} onChange={(e) => set({ client_name: e.target.value })} placeholder="e.g. Rakesh Sharma" />
          </Field>
          <Field label="Trade name of business" error={errors.business_name?.[0]} hint="The legal entity / business you're providing services for.">
            <input className="input" value={form.business_name} onChange={(e) => set({ business_name: e.target.value })} placeholder="e.g. Sharma Traders Pvt Ltd" />
          </Field>
          <Field label="MD Mobile" error={errors.business_phone?.[0]} hint="The MD / owner's mobile — their portal username is derived from it.">
            <input className="input" value={form.business_phone} onChange={(e) => set({ business_phone: e.target.value })} placeholder="+919876543210" />
          </Field>
          <Field label="Email" error={errors.business_email?.[0]}>
            <input className="input" type="email" value={form.business_email} onChange={(e) => set({ business_email: e.target.value })} />
          </Field>
          <Field label="Concerned person name" error={errors.concerned_person_name?.[0]} hint="Secondary contact at the client (accountant, manager, etc.).">
            <input className="input" value={form.concerned_person_name} onChange={(e) => set({ concerned_person_name: e.target.value })} placeholder="e.g. Suresh Kumar" />
          </Field>
          <Field label="Concerned person mobile" error={errors.concerned_person_phone?.[0]}>
            <input className="input" value={form.concerned_person_phone} onChange={(e) => set({ concerned_person_phone: e.target.value })} placeholder="+919876543210" />
          </Field>
          <Field label="Address" error={errors.address?.[0]}>
            <textarea className="textarea" rows={3} value={form.address} onChange={(e) => set({ address: e.target.value })} placeholder="Street, area, city, state, pincode" />
          </Field>
          <Field label="Description (optional)">
            <textarea className="textarea" value={form.description} onChange={(e) => set({ description: e.target.value })} />
          </Field>
          <div className="row gap-sm">
            <input
              id="client-active"
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => set({ is_active: e.target.checked })}
            />
            <label htmlFor="client-active" style={{ flex: 1, cursor: "pointer", marginBottom: 0 }}>
              <div className="t-primary text-sm fw-500">Active</div>
              <div className="t-muted text-xs" style={{ marginTop: 2 }}>
                Inactive clients can't sign in (if a login exists) and aren't surfaced in oversight defaults.
              </div>
            </label>
          </div>
        </Section>

        <Section title="Income tax">
          <Field label="PAN" error={errors.pan?.[0]} hint="10 chars: 5 letters + 4 digits + 1 letter.">
            <input className="input t-mono" value={form.pan} onChange={(e) => set({ pan: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" maxLength={10} />
          </Field>
          <Field label="IT portal password" error={errors.it_password?.[0]}>
            <PasswordInput value={form.it_password} onChange={(v) => set({ it_password: v })} />
          </Field>
          <Field label="ITR type" error={errors.itr_type?.[0]}>
            <select className="input" value={form.itr_type} onChange={(e) => set({ itr_type: e.target.value })}>
              <option value="">—</option>
              {ITR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Regime" error={errors.regime?.[0]}>
            <select className="input" value={form.regime} onChange={(e) => set({ regime: e.target.value })}>
              <option value="">—</option>
              <option value="old">Old regime</option>
              <option value="new">New regime</option>
            </select>
          </Field>
        </Section>

        <Section title="GST">
          <Field label="GSTIN" error={errors.gstin?.[0]} hint="15 chars: state code + PAN + entity + Z + check.">
            <input className="input t-mono" value={form.gstin} onChange={(e) => set({ gstin: e.target.value.toUpperCase() })} placeholder="22ABCDE1234F1Z5" maxLength={15} />
          </Field>
          <Field label="State name" error={errors.state_name?.[0]} hint="Used on the Buyer block of every invoice.">
            <input className="input" value={form.state_name} onChange={(e) => set({ state_name: e.target.value })} placeholder="Andhra Pradesh" maxLength={100} />
          </Field>
          <Field label="State code" error={errors.state_code?.[0]} hint="2-digit GST state code (first 2 chars of GSTIN).">
            <input className="input t-mono" value={form.state_code} onChange={(e) => set({ state_code: e.target.value })} placeholder="37" inputMode="numeric" maxLength={3} />
          </Field>
          <Field label="GST portal ID" error={errors.gst_id?.[0]}>
            <input className="input" value={form.gst_id} onChange={(e) => set({ gst_id: e.target.value })} />
          </Field>
          <Field label="GST portal password" error={errors.gst_password?.[0]}>
            <PasswordInput value={form.gst_password} onChange={(v) => set({ gst_password: v })} />
          </Field>
          <Field label="Filing frequency" error={errors.gst_frequency?.[0]}>
            <select className="input" value={form.gst_frequency} onChange={(e) => set({ gst_frequency: e.target.value })}>
              <option value="">—</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </Field>
          <Field label="Scheme" error={errors.gst_scheme?.[0]}>
            <select className="input" value={form.gst_scheme} onChange={(e) => set({ gst_scheme: e.target.value })}>
              <option value="">—</option>
              <option value="regular">Regular</option>
              <option value="composition">Composition</option>
            </select>
          </Field>
        </Section>

        <Section title="Entity">
          <Field label="Type of entity" error={errors.entity_type?.[0]}>
            <select className="input" value={form.entity_type} onChange={(e) => set({ entity_type: e.target.value })}>
              <option value="">—</option>
              {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Authorised signatory" error={errors.authorised_signatory?.[0]}>
            <input className="input" value={form.authorised_signatory} onChange={(e) => set({ authorised_signatory: e.target.value })} />
          </Field>
        </Section>

        <Section title="TAN / TRACES">
          <Field label="TAN no." error={errors.tan_no?.[0]} hint="10 chars: 4 letters + 5 digits + 1 letter.">
            <input className="input t-mono" value={form.tan_no} onChange={(e) => set({ tan_no: e.target.value.toUpperCase() })} placeholder="ABCD12345E" maxLength={10} />
          </Field>
          <Field label="TAN IT password" error={errors.tan_it_password?.[0]}>
            <PasswordInput value={form.tan_it_password} onChange={(v) => set({ tan_it_password: v })} />
          </Field>
          <Field label="TRACES ID" error={errors.tan_traces_id?.[0]}>
            <input className="input" value={form.tan_traces_id} onChange={(e) => set({ tan_traces_id: e.target.value })} />
          </Field>
          <Field label="TRACES password" error={errors.tan_traces_password?.[0]}>
            <PasswordInput value={form.tan_traces_password} onChange={(v) => set({ tan_traces_password: v })} />
          </Field>
        </Section>
      </div>
    </Drawer>
  );
}

function Section({ title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card" style={{ padding: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="row"
        style={{
          width: "100%", padding: "10px 12px", background: "var(--surface-subtle, transparent)",
          border: 0, borderBottom: open ? "1px solid var(--border)" : 0,
          cursor: "pointer", textAlign: "left",
        }}
      >
        <Icon name={open ? "chevron-down" : "chevron-right"} size={12} />
        <strong style={{ marginLeft: 6 }}>{title}</strong>
      </button>
      {open && <div className="col gap-md" style={{ padding: 12 }}>{children}</div>}
    </div>
  );
}

function PasswordInput({ value, onChange }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="row" style={{ alignItems: "stretch" }}>
      <input
        className="input"
        style={{ flex: 1 }}
        type={shown ? "text" : "password"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="off"
      />
      <button
        type="button"
        className="btn btn-secondary"
        onClick={() => setShown((s) => !s)}
        style={{ marginLeft: 6 }}
        title={shown ? "Hide" : "Show"}
      >
        <Icon name={shown ? "eye-off" : "eye"} size={14} />
      </button>
    </div>
  );
}

function Field({ label, error, hint, children }) {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      {children}
      {hint && !error && <div className="field-hint">{hint}</div>}
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}
